// Simple mock API using localStorage to simulate backend endpoints for v0
import { v4 as uuidv4 } from "uuid";
import { aggregateHistoryByDay } from "../utils/usageUtils";

const STORAGE_KEY = "wascaner_state_v0";

function loadState() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const initial = {
      users: {},
      company_configs: {},
      scanners: {},
      sessions: {},
      tokens: {},
      pendingVerifications: {},
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
    return initial;
  }
  return JSON.parse(raw);
}

function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// Auth functions
export function signup({
  name,
  company_name,
  email,
  password,
  phone,
  country,
  language,
}) {
  const state = loadState();

  // Check if email already exists
  const existingUser = Object.values(state.users).find(
    (u) => u.email === email.toLowerCase()
  );
  if (existingUser) {
    return Promise.resolve({
      success: false,
      message: "Este correo electrónico ya está registrado.",
    });
  }

  const userId = uuidv4();
  const companyId = uuidv4();
  const verificationCode =
    process.env.NODE_ENV === "test"
      ? "123456"
      : Math.floor(100000 + Math.random() * 900000).toString();

  // Store pending verification
  state.pendingVerifications[userId] = {
    userId,
    companyId,
    name,
    company_name,
    email: email.toLowerCase(),
    password,
    phone,
    country,
    language,
    code: verificationCode,
    expiresAt: Date.now() + 10 * 60 * 1000, // 10 minutes
  };

  saveState(state);

  // In mock mode, log the code for testing
  console.log(`[MOCK] Verification code for ${phone}: ${verificationCode}`);

  return Promise.resolve({
    success: true,
    user_id: userId,
    company_id: companyId,
    message:
      "Registro exitoso. Se ha enviado un código de verificación a tu WhatsApp.",
  });
}

export function verifyCode({ user_id, code }) {
  const state = loadState();
  const pending = state.pendingVerifications[user_id];

  if (!pending) {
    return Promise.resolve({
      success: false,
      message: "Usuario no encontrado.",
    });
  }

  if (Date.now() > pending.expiresAt) {
    return Promise.resolve({
      success: false,
      message: "El código ha expirado.",
    });
  }

  if (pending.code !== code) {
    return Promise.resolve({ success: false, message: "Código incorrecto." });
  }

  // Create the user
  const user = {
    id: pending.userId,
    user_id: pending.userId,
    company_id: pending.companyId,
    name: pending.name,
    company_name: pending.company_name,
    email: pending.email,
    phone: pending.phone,
    phones: [pending.phone],
    country: pending.country,
    language: pending.language,
    phone_verified: true,
    onboarding_completed: false,
    subscription_plan: "free", // New free users are on free plan
    roles: ["admin", "owner"],
    createdAt: Date.now(),
  };

  state.users[pending.userId] = user;
  state.scanners[pending.userId] = [];
  state.tokens[pending.userId] = 20; // 20 free tokens

  // Clean up pending verification
  delete state.pendingVerifications[user_id];

  saveState(state);

  return Promise.resolve({
    success: true,
    token: "mock_jwt_token_" + pending.userId,
    token_type: "Bearer",
    user: user,
    message: "Verificación exitosa. ¡Bienvenido a WAScaner!",
  });
}

export function login(email, password) {
  const state = loadState();
  const user = Object.values(state.users).find(
    (u) => u.email === email.toLowerCase()
  );

  if (!user) {
    return Promise.resolve({
      success: false,
      message: "Credenciales inválidas.",
    });
  }

  // In mock mode, we don't verify password
  return Promise.resolve({
    token: "mock_jwt_token_" + user.id,
    token_type: "Bearer",
    user: {
      ...user,
      force_password_change: user.force_password_change || false,
    },
  });
}

// User functions
export function createUser({
  email,
  phoneNumbers = [],
  phones = [],
  lang = "es",
}) {
  const state = loadState();
  const id = uuidv4();
  // Support both phoneNumbers and phones to maintain backwards compatibility
  const normalizedPhones = phones.length ? phones : phoneNumbers;
  const phone =
    normalizedPhones && normalizedPhones.length
      ? normalizedPhones[0]
      : undefined;
  const user = {
    id,
    email,
    phoneNumbers: normalizedPhones,
    phones: normalizedPhones,
    phone,
    lang,
    subscription_plan: "free", // Default to free plan
    createdAt: Date.now(),
  };
  state.users[id] = user;
  state.scanners[id] = [];
  state.tokens[id] = 100; // starter tokens
  saveState(state);
  return Promise.resolve(user);
}

export function getUser(id) {
  const state = loadState();
  let userId = id;
  if (!userId) {
    try {
      const token =
        typeof localStorage !== "undefined" && localStorage.getItem("token");
      if (token && token.startsWith("mock_jwt_token_")) {
        userId = token.replace("mock_jwt_token_", "");
      }
    } catch (err) {
      // ignore
    }
  }
  if (!userId) return Promise.resolve(null);
  return Promise.resolve(state.users[userId]);
}

export function updateUser(arg1, arg2) {
  const state = loadState();
  let id = null;
  let patch = null;
  // Support both signatures: (id, patch) and (patch) where the current user is inferred from the token
  if (arg2 === undefined && typeof arg1 === "object" && arg1 !== null) {
    patch = arg1;
    // Try to infer current user from mock token in localStorage: 'mock_jwt_token_<id>'
    try {
      const token =
        typeof localStorage !== "undefined" && localStorage.getItem("token");
      if (token && token.startsWith("mock_jwt_token_")) {
        id = token.replace("mock_jwt_token_", "");
      }
    } catch (err) {
      // ignore; fallback below
    }
    // Fallback to first user in state
    if (!id) {
      id = Object.keys(state.users || {})[0];
    }
  } else {
    id = arg1;
    patch = arg2;
  }
  if (!id) return Promise.reject(new Error("No user available to update"));

  // Simulate global phone uniqueness across organizations (company_id)
  if (patch && patch.phones !== undefined) {
    const currentUser = state.users[id];
    const targetCompanyId = currentUser?.company_id;
    const requestedPhones = (
      Array.isArray(patch.phones) ? patch.phones : [patch.phones]
    )
      .filter(Boolean)
      .map((p) => String(p).trim())
      .filter(Boolean);

    const normalize = (p) => `+${String(p || "").replace(/\D/g, "")}`;
    for (const phone of requestedPhones) {
      const n = normalize(phone);
      const other = Object.values(state.users || {}).find((u) => {
        if (!u) return false;
        if (u === currentUser) return false;
        if (targetCompanyId && u.company_id && u.company_id === targetCompanyId)
          return false;
        const phs = Array.isArray(u.phones)
          ? u.phones
          : [u.phone].filter(Boolean);
        return (phs || []).some((pp) => normalize(pp) === n);
      });
      if (other) {
        return Promise.reject({
          response: {
            status: 409,
            data: {
              success: false,
              error_code: "PHONE_IN_USE",
              phone: n,
              existing_company_id: other.company_id || "mock_company",
            },
          },
        });
      }
    }
    // Normalize stored form
    patch.phones = requestedPhones.map((p) => normalize(p));
  }

  if (patch.password) {
    // For mock, set a simple plain-text password (dev only). In production the backend will hash
    state.users[id].password_hash = patch.password;
    state.users[id].force_password_change = false; // Clear flag if was forced
  }
  state.users[id] = { ...state.users[id], ...patch };
  saveState(state);
  return Promise.resolve(state.users[id]);
}

export function requestPhoneTransferCode({ phone }) {
  const state = loadState();
  const normalized = `+${String(phone || "").replace(/\D/g, "")}`;
  state.pendingPhoneTransfers = state.pendingPhoneTransfers || {};
  const code = Math.floor(100000 + Math.random() * 900000).toString();
  state.pendingPhoneTransfers[normalized] = {
    phone: normalized,
    code,
    expiresAt: Date.now() + 10 * 60 * 1000,
  };
  saveState(state);
  console.log(`[MOCK] Phone transfer code for ${normalized}: ${code}`);
  return Promise.resolve({
    success: true,
    status: "code_sent",
    phone: normalized,
  });
}

export function verifyPhoneTransferCode({ phone, code }) {
  const state = loadState();
  const normalized = `+${String(phone || "").replace(/\D/g, "")}`;
  const pending = state.pendingPhoneTransfers?.[normalized];
  if (!pending) {
    return Promise.reject({
      response: {
        status: 404,
        data: { success: false, error_code: "TRANSFER_NOT_FOUND" },
      },
    });
  }
  if (Date.now() > pending.expiresAt) {
    return Promise.reject({
      response: {
        status: 400,
        data: { success: false, error_code: "CODE_EXPIRED" },
      },
    });
  }
  if (String(code || "").trim() !== pending.code) {
    return Promise.reject({
      response: {
        status: 400,
        data: { success: false, error_code: "CODE_INVALID" },
      },
    });
  }
  delete state.pendingPhoneTransfers[normalized];
  saveState(state);
  return Promise.resolve({
    success: true,
    status: "transferred",
    phone: normalized,
  });
}

export function requestPasswordReset({ email }) {
  const state = loadState();
  const user = Object.values(state.users).find(
    (u) => u.email === email?.toLowerCase()
  );
  // For security, always respond success even if not found
  if (!user) return Promise.resolve({ success: true });
  const generateTempPassword = (language = "es") => {
    const lang = String(language || "").toLowerCase();
    const sets = lang.startsWith("es")
      ? {
          w1: ["arbol", "rio", "mar", "monte", "bosque", "sol", "luna", "nube"],
          w2: ["verde", "azul", "rojo", "claro", "suave", "rapido", "feliz"],
          w3: ["pajaro", "gato", "perro", "zorro", "lobo", "tigre", "leon"],
        }
      : {
          w1: [
            "tree",
            "river",
            "ocean",
            "mountain",
            "forest",
            "sun",
            "moon",
            "cloud",
          ],
          w2: ["green", "blue", "red", "bright", "soft", "quick", "happy"],
          w3: ["bird", "cat", "dog", "fox", "wolf", "tiger", "lion"],
        };
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    return `${pick(sets.w1)}${pick(sets.w2)}${pick(sets.w3)}`;
  };

  const tempPassword = generateTempPassword(user.language || "es");
  user.password_hash = tempPassword; // in mock we store plain
  user.force_password_change = true;
  saveState(state);
  console.log(`[MOCK] Temp password for ${email}: ${tempPassword}`);
  return Promise.resolve({ success: true });
}

export function listScanners(userId) {
  const state = loadState();
  let id = userId;
  if (!id) {
    try {
      const token =
        typeof localStorage !== "undefined" && localStorage.getItem("token");
      if (token && token.startsWith("mock_jwt_token_")) {
        id = token.replace("mock_jwt_token_", "");
      }
    } catch (err) {
      // ignore
    }
  }
  // Fallback: return empty array
  if (!id) return Promise.resolve([]);
  return Promise.resolve(state.scanners[id] || []);
}

// Un escáner por id (el detalle lo pide así en la API remota). Sin él, /scanners/:id no funcionaba en modo mock.
export function getScanner(scannerId) {
  const state = loadState();
  let id = null;
  try {
    const token = typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) id = token.replace("mock_jwt_token_", "");
  } catch (err) {
    // ignore
  }
  const lists = id ? [state.scanners[id] || []] : Object.values(state.scanners || {});
  for (const list of lists) {
    const found = list.find((s) => s.id === scannerId);
    if (found) return Promise.resolve(found);
  }
  return Promise.reject(Object.assign(new Error("Scanner not found"), { response: { status: 404 } }));
}

export function createScanner(arg1, arg2) {
  const state = loadState();
  let userId = null;
  let scanner = null;
  if (arg2 === undefined && typeof arg1 === "object") {
    // Called as createScanner(scanner)
    scanner = arg1;
    try {
      const token =
        typeof localStorage !== "undefined" && localStorage.getItem("token");
      if (token && token.startsWith("mock_jwt_token_")) {
        userId = token.replace("mock_jwt_token_", "");
      }
    } catch (err) {
      // ignore
    }
  } else {
    userId = arg1;
    scanner = arg2;
  }
  if (!userId) {
    // fallback: pick first user
    userId = Object.keys(state.users || {})[0];
  }
  const s = { id: uuidv4(), ...scanner, createdAt: Date.now(), history: [] };
  state.scanners[userId] = (state.scanners[userId] || []).concat([s]);
  saveState(state);
  return Promise.resolve(s);
}

export function updateScanner(userId, scannerId, patch) {
  const state = loadState();
  const arr = state.scanners[userId] || [];
  const idx = arr.findIndex((s) => s.id === scannerId);
  if (idx === -1) return Promise.reject(new Error("Not found"));
  arr[idx] = { ...arr[idx], ...patch };
  state.scanners[userId] = arr;
  saveState(state);
  return Promise.resolve(arr[idx]);
}

export function deleteScanner(userId, scannerId) {
  const state = loadState();
  state.scanners[userId] = (state.scanners[userId] || []).filter(
    (s) => s.id !== scannerId
  );
  saveState(state);
  return Promise.resolve();
}

export function getTokens(userId) {
  const state = loadState();
  return Promise.resolve(state.tokens[userId] || 0);
}

export function addTokens(userId, amount) {
  const state = loadState();
  state.tokens[userId] = (state.tokens[userId] || 0) + amount;
  saveState(state);
  return Promise.resolve(state.tokens[userId]);
}

export function saveCompanyBilling(billingInfo) {
  const state = loadState();
  // In mock mode we require a logged in user - try to resolve from currently stored users
  // We'll pick the first user for simplicity (mock environment)
  const userIds = Object.keys(state.users || {});
  const userId = userIds.length > 0 ? userIds[0] : null;
  if (!userId) {
    return Promise.reject(new Error("No user available in mock environment"));
  }
  const user = state.users[userId];
  const companyId = user?.company_id || uuidv4();
  // Ensure mapping exists
  if (!state.company_configs) state.company_configs = {};
  const current = state.company_configs[companyId] || {};
  const updated = {
    ...current,
    company_id: companyId,
    name: billingInfo.company_name || current.name || user.company_name || "",
    billing_contact: billingInfo.billing_contact,
    billing_email: billingInfo.billing_email,
    vat_id: billingInfo.vat_id,
    address: billingInfo.address || {},
    updated_at: new Date().toISOString(),
  };
  state.company_configs[companyId] = updated;
  saveState(state);
  return Promise.resolve({ success: true, company: updated });
}

// ============= Subscription & Billing (mock) =============
export function getSubscription() {
  const state = loadState();
  // Infer current user from mock token if available
  let userId = null;
  try {
    const token =
      typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) {
      userId = token.replace("mock_jwt_token_", "");
    }
  } catch (err) {
    // ignore
  }
  if (!userId) return Promise.resolve(null);
  const user = state.users[userId];
  if (!user) return Promise.resolve(null);
  // Minimal subscription object used by components/tests
  const subscription = {
    subscription_plan: user.subscription_plan || "free",
    subscription_status: "active",
    stripe_customer_id: user.stripe_customer_id || null,
    billing_email: user.billing_email || null,
    billing_contact: user.billing_contact || null,
    company_name: user.company_name || null,
    vat_id: user.vat_id || null,
    billing_address: user.billing_address || null,
  };
  return Promise.resolve(subscription);
}

export function activateFreePlan() {
  // Set user's plan to free
  const state = loadState();
  let userId = Object.keys(state.users || {})[0];
  try {
    const token =
      typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) {
      userId = token.replace("mock_jwt_token_", "");
    }
  } catch (err) {}
  if (!userId) return Promise.reject(new Error("No user available"));
  state.users[userId].subscription_plan = "free";
  saveState(state);
  return Promise.resolve({ success: true });
}

export function createCheckoutSession(payload) {
  const { planId, billingCycle, replaceExistingSubscription } = payload;
  // Return a fake checkout URL
  return Promise.resolve({
    checkoutUrl: `https://checkout.mock/${planId}/${billingCycle}?replace=${
      replaceExistingSubscription ? "1" : "0"
    }`,
  });
}

export function createCreditPackCheckout(payload) {
  const { packId } = payload;
  return Promise.resolve({
    checkoutUrl: `https://checkout.mock/pack/${packId}`,
  });
}

export function fixFreeCredits() {
  // Simulate adjusting pack credits by removing initial free grant
  const state = loadState();
  let userId = Object.keys(state.users || {})[0];
  try {
    const token =
      typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) {
      userId = token.replace("mock_jwt_token_", "");
    }
  } catch (err) {}
  const user = state.users[userId];
  if (!user) return Promise.reject(new Error("No user available"));
  const companyId = user.company_id;
  const company = state.companies[companyId];
  if (!company || !company.free_credits_granted) {
    return Promise.resolve({
      success: false,
      message: "No free grant present",
    });
  }
  const freeGrant = Number(process.env.INITIAL_FREE_TOKENS || 20);
  company.pack_credits_balance = Math.max(
    0,
    (company.pack_credits_balance || 0) - freeGrant
  );
  company.updated_at = new Date().toISOString();
  saveState(state);
  return Promise.resolve({
    success: true,
    newPack: company.pack_credits_balance,
  });
}

export function getSubscriptionPricing() {
  return Promise.resolve({
    version: "1.2.0",
    last_updated: "2025-12-15",
    currency_base: "EUR",
    supported_currencies: ["EUR", "USD", "GBP", "CAD", "AUD"],
    billing_model: "monthly_reset",
    plans: {
      free: {
        id: "free",
        name: { es: "Plan Gratuito", en: "Free Plan" },
        description: { es: "Prueba gratuita", en: "Free trial" },
        monthly_price_eur: 0,
        yearly_price_eur: 0,
        credits_per_month: 20,
        features: { unlimited_scanners: false, max_scanners: 3 },
        stripe_price_ids: { monthly: null, yearly: null },
        requires_payment_method: false,
      },
      mini: {
        id: "mini",
        name: { es: "Plan Mini", en: "Mini Plan" },
        description: { es: "Para equipos", en: "For teams" },
        monthly_price_eur: 45,
        yearly_price_eur: 486,
        credits_per_month: 150,
        features: { unlimited_scanners: true },
        stripe_price_ids: {
          monthly: "price_mini_monthly",
          yearly: "price_mini_yearly",
        },
        requires_payment_method: true,
      },
      starter: {
        id: "starter",
        name: { es: "Plan Starter", en: "Starter Plan" },
        description: { es: "Para empresas", en: "For businesses" },
        monthly_price_eur: 100,
        yearly_price_eur: 1080,
        credits_per_month: 500,
        features: { unlimited_scanners: true },
        stripe_price_ids: {
          monthly: "price_starter_monthly",
          yearly: "price_starter_yearly",
        },
        requires_payment_method: true,
        popular: true,
      },
    },
    additional_features: {
      credit_packs: {
        small: {
          credits: 250,
          price_eur: 70,
          description: { es: "Pack de 250 créditos", en: "250 credits pack" },
          stripe_price_id: "price_pack_small",
        },
      },
    },
    policies: { pages_per_credit: 1 },
  });
}

export function cancelSubscription() {
  const state = loadState();
  let userId = Object.keys(state.users || {})[0];
  try {
    const token =
      typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) {
      userId = token.replace("mock_jwt_token_", "");
    }
  } catch (err) {}
  if (!userId) return Promise.reject(new Error("No user available"));
  state.users[userId].subscription_plan = "free";
  saveState(state);
  return Promise.resolve({ success: true });
}

export function getInvoices() {
  // Mock: return empty list
  return Promise.resolve([]);
}

export function changePlan({ priceId, planId, billingCycle }) {
  const state = loadState();
  let userId = Object.keys(state.users || {})[0];
  try {
    const token =
      typeof localStorage !== "undefined" && localStorage.getItem("token");
    if (token && token.startsWith("mock_jwt_token_")) {
      userId = token.replace("mock_jwt_token_", "");
    }
  } catch (err) {}
  if (!userId) return Promise.reject(new Error("No user available"));
  state.users[userId].subscription_plan = planId;
  saveState(state);
  return Promise.resolve({ success: true });
}

export function getCustomerPortalUrl() {
  return Promise.resolve({ url: "https://portal.mock" });
}

// Tickets mock functions
export function createTicket(
  userId,
  { subject, message: messageContent, priority = "normal" }
) {
  const state = loadState();
  const ticketId = uuidv4();
  const now = new Date().toISOString();
  const ticket = {
    ticket_id: ticketId,
    subject,
    status: "open",
    priority,
    userId,
    createdAt: now,
    updatedAt: now,
  };
  const messageId = uuidv4();
  const message = {
    message_id: messageId,
    ticket_id: ticketId,
    from: "user",
    userId,
    content: messageContent,
    createdAt: now,
  };
  if (!state.tickets) state.tickets = {};
  if (!state.userTickets) state.userTickets = {};
  state.tickets[ticketId] = { ticket, messages: [message] };
  state.userTickets[userId] = (state.userTickets[userId] || []).concat([
    ticket,
  ]);
  saveState(state);
  return Promise.resolve({ success: true, ticket, message });
}

export function listTickets(userId) {
  const state = loadState();
  const tickets = state.userTickets?.[userId] || [];
  return Promise.resolve({ success: true, tickets });
}

export function getTicket(userId, ticketId) {
  const state = loadState();
  const entry = state.tickets?.[ticketId];
  if (!entry) return Promise.resolve({ success: false, message: "Not found" });
  // allow only owner in mock mode
  if (entry.ticket.userId !== userId)
    return Promise.resolve({ success: false, message: "Forbidden" });
  return Promise.resolve({
    success: true,
    ticket: entry.ticket,
    messages: entry.messages || [],
  });
}

export function postTicketMessage(userId, ticketId, messageContent) {
  const state = loadState();
  const entry = state.tickets?.[ticketId];
  if (!entry) return Promise.resolve({ success: false, message: "Not found" });
  if (entry.ticket.userId !== userId)
    return Promise.resolve({ success: false, message: "Forbidden" });
  const messageId = uuidv4();
  const now = new Date().toISOString();
  const message = {
    message_id: messageId,
    ticket_id: ticketId,
    from: "user",
    userId,
    content: messageContent,
    createdAt: now,
  };
  entry.messages = entry.messages || [];
  entry.messages.push(message);
  entry.ticket.updatedAt = now;
  saveState(state);
  return Promise.resolve({ success: true, message });
}

// Simulate processing: count pages or images => tokens; extract fields by pattern.
export function processUpload(userId, scannerId, file) {
  // Simulate async processing: return queued response immediately and finish processing in background
  return new Promise((resolve, reject) => {
    const state = loadState();
    const tokensLeft = state.tokens[userId] || 0;
    // fake page count based on file size / 100k
    const pages = Math.max(1, Math.round((file.size || 10000) / 100000));
    if (tokensLeft < pages) return reject(new Error("Insufficient tokens"));
    const scanner = (state.scanners[userId] || []).find(
      (s) => s.id === scannerId
    );
    const fields = (scanner && scanner.fields) || [];

    // Create placeholder history entry
    const resultId = uuidv4();
    const event = {
      id: resultId,
      fileName: file.name || "file",
      pages,
      result: null,
      status: "queued",
      createdAt: Date.now(),
    };
    if (scanner) {
      scanner.history = scanner.history || [];
      scanner.history.push(event);
    }
    saveState(state);

    // Return queued immediate response
    resolve({
      success: true,
      requestId: resultId,
      resultId,
      status: "queued",
      tokensRequired: pages,
    });

    // Simulate background processing after a short delay and update history
    setTimeout(() => {
      // Build fake data: for each field return a string with field name
      const extracted = {};
      fields.forEach((f) => {
        extracted[f.name] = `Example: extracted ${f.name}`;
      });
      const result = { pages, extracted };
      // Update history
      const foundScanner = (state.scanners[userId] || []).find(
        (s) => s.id === scannerId
      );
      if (foundScanner) {
        const idx = (foundScanner.history || []).findIndex(
          (h) => h.id === resultId
        );
        if (idx !== -1) {
          foundScanner.history[idx] = {
            ...foundScanner.history[idx],
            result,
            status: "done",
          };
        }
      }
      // Debit tokens and save state
      state.tokens[userId] = tokensLeft - pages;
      saveState(state);
    }, 1500);
  });
}

export function getScannerHistory(arg1, arg2, _params = {}) {
  const state = loadState();
  let userId = null;
  let scannerId = arg1;

  // Legacy signature compatibility: (userId, scannerId)
  if (typeof arg2 === "string" && arg2.length > 0) {
    userId = arg1;
    scannerId = arg2;
  }

  const findScannerByUser = () => {
    if (!userId) return null;
    return (state.scanners[userId] || []).find((s) => s.id === scannerId);
  };

  let scanner = findScannerByUser();
  if (!scanner) {
    // Fallback: search across all users
    scanner = Object.values(state.scanners || {})
      .flat()
      .find((s) => s.id === scannerId);
  }

  const history = (scanner && scanner.history) || [];
  return Promise.resolve({
    success: true,
    items: history,
    count: history.length,
    nextCursor: null,
  });
}

export function getUsageSummary(days = 30) {
  const state = loadState();
  // gather all history entries across all scanners
  const allHistory = Object.values(state.scanners || {}).flatMap((arr) =>
    (arr || []).flatMap((s) => s.history || [])
  );
  const aggregated = aggregateHistoryByDay(allHistory, Number(days) || 30);
  return Promise.resolve({ success: true, items: aggregated });
}

// ============= API Keys (mock) =============
export function getApiKey() {
  return Promise.resolve({
    success: true,
    has_api_key: true,
    api_key_prefix: "wsk_mock",
    created_at: new Date().toISOString(),
    last_used_at: null,
  });
}

export function regenerateApiKey() {
  const newKey = `wsk_mock${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`;
  return Promise.resolve({
    success: true,
    api_key: newKey,
    api_key_prefix: newKey.slice(0, 8),
    created_at: new Date().toISOString(),
    message: "API key regenerated successfully. Previous key is now invalid.",
  });
}

// Simple session helper for dev - now deprecated, use login with password
export function legacyLogin(email) {
  const state = loadState();
  const userId = Object.keys(state.users).find(
    (id) => state.users[id].email === email
  );
  if (userId) return Promise.resolve(state.users[userId]);
  return createUser({ email });
}

export function getReferrals() {
  return Promise.resolve({
    success: true,
    code: "mock0000",
    link: "https://whappscan.com/en/?ref=mock0000",
    invited: 0,
    rewarded: 0,
    pending: 0,
    pages_earned: 0,
    reward_pages: 20,
    monthly_cap: 10,
    monthly_cap_left: 10,
  });
}

const mockApi = {
  // Auth
  signup,
  verifyCode,
  login,
  // Users
  createUser,
  getUser,
  updateUser,
  requestPhoneTransferCode,
  verifyPhoneTransferCode,
  // Scanners
  listScanners,
  createScanner,
  updateScanner,
  deleteScanner,
  // Tokens
  getTokens,
  addTokens,
  // Processing
  processUpload,
  getScannerHistory,
  getScanner,
  getUsageSummary,
  getReferrals,
  // Tickets
  createTicket,
  listTickets,
  getTicket,
  postTicketMessage,
  // Subscription & Billing
  getSubscription,
  getSubscriptionPricing,
  activateFreePlan,
  createCheckoutSession,
  createCreditPackCheckout,
  cancelSubscription,
  getInvoices,
  changePlan,
  getCustomerPortalUrl,
  // API Keys
  getApiKey,
  regenerateApiKey,
  // Legacy
  legacyLogin,
  // Billing / Company config
  saveCompanyBilling,
};

export default mockApi;
